import { useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { resetSponsorDemo, signOut, useSponsorStore } from '@/lib/sponsor/store';
import { parseSponsorSelection } from '@/lib/sponsor/selection';
import { defaultSelection, type Selection, type SponsorContext } from './context';
import BrandLogo from '@/components/common/BrandLogo';

const SEL_KEY = 'lb-sponsor-selection';

function loadSelection(): Selection {
  try {
    return parseSponsorSelection(sessionStorage.getItem(SEL_KEY));
  } catch {
    /* ignore */
  }
  return defaultSelection;
}

export default function SponsorLayout() {
  const store = useSponsorStore();
  const [selection, setSel] = useState<Selection>(loadSelection);
  const account = store.accounts.find((a) => a.id === store.currentAccountId) ?? null;
  const hasOrder = !!account && store.orders.some((o) => o.accountId === account.id);

  const setSelection = (fn: (s: Selection) => Selection) =>
    setSel((prev) => {
      const next = fn(prev);
      try {
        sessionStorage.setItem(SEL_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });

  const steps = [
    { to: '/sponsor', label: '1 · Package', end: true, disabled: false },
    { to: '/sponsor/register', label: '2 · Sign up', end: false, disabled: !!account },
    { to: '/sponsor/checkout', label: '3 · Pay', end: false, disabled: !account },
    { to: '/sponsor/dashboard', label: '4 · Dashboard', end: false, disabled: !hasOrder },
  ];

  const initials = (account?.company ?? 'Guest')
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="min-h-screen bg-background-100 text-foreground-950">
      <header className="sticky top-0 z-30 bg-background-50/95 backdrop-blur border-b border-background-200">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <BrandLogo className="w-9 h-9" />
            <span className="hidden sm:block font-heading font-bold">LoyaltyBridge <span className="font-medium text-foreground-500">Sponsors</span></span>
          </Link>
          <nav className="flex-1 flex gap-1 overflow-x-auto" aria-label="Sponsor steps">
            {steps.map((s) =>
              s.disabled ? (
                <span key={s.to} className="px-3 py-1.5 rounded-md text-sm text-foreground-400 whitespace-nowrap" aria-disabled="true">
                  {s.label}
                </span>
              ) : (
                <NavLink
                  key={s.to}
                  to={s.to}
                  end={s.end}
                  className={({ isActive }) =>
                    `px-3 py-1.5 rounded-md text-sm whitespace-nowrap transition-colors ${
                      isActive ? 'bg-background-200 font-semibold text-foreground-950' : 'text-foreground-600 hover:text-foreground-950'
                    }`
                  }
                >
                  {s.label}
                </NavLink>
              ),
            )}
          </nav>
          <div className="flex items-center gap-2 shrink-0">
            <span className="w-9 h-9 rounded-lg bg-foreground-950 text-background-50 grid place-items-center text-xs font-bold">{initials}</span>
            <div className="hidden md:flex flex-col leading-tight">
              <span className="text-sm font-semibold">{account?.company ?? 'Guest'}</span>
              <span className="text-xs text-foreground-500">
                {account ? (account.status === 'verified' ? 'Verified sponsor' : account.status === 'rejected' ? 'Not approved' : 'Registered') : 'Not signed up'}
              </span>
            </div>
            {account && (
              <button type="button" onClick={signOut} className="ml-1 text-xs text-foreground-500 hover:text-foreground-950 cursor-pointer">
                Sign out
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-8">
        <Outlet context={{ selection, setSelection } satisfies SponsorContext} />
      </main>

      <footer className="max-w-6xl mx-auto px-4 md:px-6 pb-10 flex flex-wrap items-center justify-between gap-3 text-xs text-foreground-500">
        <span>Demo data · prices are examples set by the manager · payments are simulated until the Loyalty API is live.</span>
        <span className="flex gap-3">
          <Link to="/dashboard/sponsors" className="underline hover:text-foreground-950">OPS view</Link>
          <button type="button" className="underline hover:text-foreground-950 cursor-pointer" onClick={() => { resetSponsorDemo(); setSelection(() => defaultSelection); }}>
            Reset demo
          </button>
        </span>
      </footer>
    </div>
  );
}
